import * as React from 'react';
import { observer } from 'mobx-react';
import { observable, action } from 'mobx';

import { DateTimePickerService } from '@app/Services/DateTimePickerService';
import DateTimeService from '@app/Services/DateTimeService';

import { DatePicker, IDatePicker } from 'office-ui-fabric-react';
import { TextCell } from './TextCell';
import { NotificationHandler } from '../ToastNotification';
import { ViewMode } from './ViewMode';

export type InlineDateCellProps<T> = {
    value: Date | null;
    row: T;
    fieldKey: keyof T;
    disabled?: boolean;
    isReadOnly?: boolean;
    minDate?: Date;
    maxDate?: Date;
    canBeEmpty?: boolean;
    onSelect: (row: T) => void;
    onConfirmation?: (row: T) => Promise<boolean>;
    onValidate: (row: T, minDate?: Date, maxDate?: Date) => boolean;
};

type BaseEntity = {
    id: string;
};

@observer
export class InlineDateCell<T extends BaseEntity> extends React.Component<InlineDateCellProps<T>, {}> {
    store: InlineDateCellStore<T>;
    props: InlineDateCellProps<T>;

    constructor(props: InlineDateCellProps<T>) {
        super(props);
        this.props = props;
        this.store = new InlineDateCellStore(props);
    }

    componentDidUpdate(prevProps: InlineDateCellProps<T>) {
        if (this.props.row.id !== prevProps.row.id || this.props.value !== prevProps.value
            || this.props.row[this.props.fieldKey] !== prevProps.row[this.props.fieldKey]
            || this.props !== prevProps) {
            this.store.updateProps(this.props);
        }
    }

    render() {
        if (this.store.mode === ViewMode.view) {
            const value = this.store.value ? DateTimeService.toUiDate(this.store.value) : '';
            return <TextCell onClick={this.store.toEditMode} value={value}/>;
        } else {
            return (
                <DatePicker
                    className="ms-field-fixed date-field"
                    componentRef={this.store.ref}
                    allowTextInput={true}
                    formatDate={DateTimePickerService.onFormatDate}
                    parseDateFromString={DateTimePickerService.onParseDateFromString}
                    onSelectDate={this.store.onSelectDate}
                    value={this.store.value || undefined}
                    minDate={this.props.minDate}
                    maxDate={this.props.maxDate}
                    onBlur={this.store.onBlur}
                    onAfterMenuDismiss={this.store.onAfterMenuDismiss}
                />
            );
        }
    }
}

class InlineDateCellStore<T> {
    @observable value: Date | null;
    @observable initValue: Date | null;
    @observable mode: string = ViewMode.view;
    @observable isInvalidRange: boolean = false;
    ref: React.RefObject<IDatePicker & { state: { isDatePickerShown: boolean} }> = React.createRef();
    private _currentProps: InlineDateCellProps<T>;

    constructor(props: InlineDateCellProps<T>) {
        this.updateProps(props);
    }

    @action
    toEditMode = () => {
        if (this._currentProps.isReadOnly || this._currentProps.disabled) return;
        this.mode = ViewMode.edit;
        setTimeout(() => {
            this.ref.current && this.ref.current.focus();
        }, 0);
    };

    @action
    updateProps(props: InlineDateCellProps<T>) {
        this._currentProps = { ...props };
        this.value = props.value || null;
    }

    @action.bound
    async onSelectDate(date?: Date | null) {
        this.mode = ViewMode.view;

        //picker date is always in LT, as soon as we work with UTC - we have to transform it
        const newDate = date && DateTimeService.date(date.getFullYear(), date.getMonth(), date.getDate()) || null;

        if (!newDate && !this.value || (newDate && this.value && DateTimeService.isSameDate(newDate, this.value))) {
            return;
        }

        if (newDate || this._currentProps.canBeEmpty) {
            this.initValue = this.value;
            this.value = newDate;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (this._currentProps.row[this._currentProps.fieldKey] as any) = this.value;

            if (this.value && this.initValue && this.value.getTime() === this.initValue.getTime()) return;
            const isValid = this._currentProps.onValidate ? this._currentProps.onValidate(this._currentProps.row, this._currentProps.minDate, this._currentProps.maxDate) : true;
            const confirmed = isValid && this._currentProps.onConfirmation ? await this._currentProps.onConfirmation(this._currentProps.row) : true;
            if (isValid && confirmed) {
                this._currentProps.onSelect(this._currentProps.row);
            } else {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (this._currentProps.row[this._currentProps.fieldKey] as any) = this.initValue;
                this.value = this.initValue;
                if (!isValid) {
                    NotificationHandler.showWarning(`Date ${newDate ? DateTimeService.toUiDate(newDate) : '<empty>'} is not valid! Value is returned to previous value.`, 'Date validation');
                }
            }
        }
    }

    onBlur = () => {
        if (!this.ref.current?.state.isDatePickerShown) {
            this.mode = ViewMode.view;
        }
    };

    onAfterMenuDismiss = () => {
        this.ref.current && this.ref.current.focus();
    };
}
